import React, { useEffect, useRef, useState } from 'react';
import Icon from '../Common/Icon';
import Logo from '../Common/Logo';
import './AuthAccessExperience.css';

const HELP_TOPICS = [
  {
    id: 'first-loop',
    icon: 'repeat',
    title: 'Start with one small loop',
    summary: 'Observe a real moment, test one change, then decide what to keep.',
    detail: 'You can use the complete Observe, Test, Reflect, and Decide path without AI. Start with one moment that matters now.'
  },
  {
    id: 'web-desktop',
    icon: 'laptop',
    title: 'Web and desktop work differently',
    summary: 'Web is online-first. Desktop Lifetime is offline-first.',
    detail: 'Web uses PocketBase as the source of truth. Desktop Lifetime saves locally first and gives you explicit manual cloud sync controls.'
  },
  {
    id: 'desktop-app',
    icon: 'monitor',
    title: 'Set up the Desktop App',
    summary: 'Lifetime adds offline-first work, local AI options, and explicit sync.',
    detail: 'Install the current build from Downloads, sign in with the account that owns Lifetime, then activate the license in Settings. Desktop encrypts records locally with a device key held by the operating-system keychain. After activation, it works offline. Cloud sync stays manual and dataset-scoped, so use Sync now and verify your records before retiring an old device.'
  },
  {
    id: 'encrypted-sync',
    icon: 'shield-check',
    title: 'Understand encrypted sync',
    summary: 'Your device encrypts personal records before cloud sync.',
    detail: 'The server stores ciphertext and cannot read your personal records. If every browser loses or replaces the old key and you do not know the previous password, the server cannot recover that key. The encrypted records remain stored but cannot be opened.'
  },
  {
    id: 'remember-key',
    icon: 'key',
    title: 'Remember the sync key',
    summary: 'Use Settings to protect cross-device access.',
    detail: 'After sign-in, open Settings, then Cloud Sync & Encryption. Check sync health and confirm Remember sync key on this device. Remembered Web keys use a rolling 30-day device window.'
  },
  {
    id: 'move-device',
    icon: 'download',
    title: 'Prepare before changing devices',
    summary: 'Sync, check the queue, and export a current backup first.',
    detail: 'Before clearing browser storage, reinstalling the desktop app, or moving to another device, confirm the sync queue is clear and export a current backup from Settings. Do not remove the old copy until the new device opens your records.'
  },
  {
    id: 'bring-journal',
    icon: 'book-open',
    title: 'Bring an existing journal',
    summary: 'Preview supported journal files before saving anything.',
    detail: 'Journal import accepts Apple Journal ZIP or HTML, Markdown or Obsidian folders, JSON, CSV, and text. Review the preview and duplicate counts before you apply the import.'
  },
  {
    id: 'ai-coach',
    icon: 'bot',
    title: 'Use AI Coach when it helps',
    summary: 'Open Coach and Guided Coaching support real-life questions and decisions.',
    detail: 'AI Coach can connect your question to relevant context you approve, while keeping personal evidence distinct from broader coaching knowledge. Open Coach starts from your words. Guided Coaching offers focused workflows. Availability depends on your plan and model setup, and the complete manual loop remains available without AI.'
  },
  {
    id: 'plans',
    icon: 'credit-card',
    title: 'Check your plan and AI access',
    summary: 'Settings shows your active plan, billing, and AI availability.',
    detail: 'Explorer includes the complete manual loop without AI. Paid plans add AI Coach and larger limits. Desktop Lifetime adds offline-first use and local model options. Open Settings to verify the active plan before changing a subscription or license.'
  },
  {
    id: 'find-anything',
    icon: 'search',
    title: 'Move around quickly',
    summary: 'Search opens features, records, and common create actions.',
    detail: 'After sign-in, press Cmd + K on macOS or Ctrl + K on Windows and Linux. Search can open Observe, Test, Decide, Journal, AI Coach, Guide, Settings, and recent records.'
  },
  {
    id: 'support',
    icon: 'life-buoy',
    title: 'Get useful support',
    summary: 'Share the platform, plan, and visible status, never secrets.',
    detail: 'Use Help & Guide or email hello@traduotech.com. Include Web or Desktop, your plan, what you expected, and the visible sync status. Never send a password, sync key, API key, or personal record content.'
  }
];

const STORY_STEPS = [
  { icon: 'compass', label: 'Observe', copy: 'Notice what is happening.' },
  { icon: 'flask-conical', label: 'Test', copy: 'Try one useful change.' },
  { icon: 'bot', label: 'AI Coach', copy: 'Get guidance when it helps.' },
  { icon: 'check-circle-2', label: 'Decide', copy: 'Keep, adjust, or stop.' }
];

export const AccountStartChoice = ({ onStartFree, onStartLifetime, onLogin }) => (
  <div className="account-start-choice">
    <span className="account-start-kicker">Create your account</span>
    <h1>How do you want to start?</h1>
    <p>Choose a starting point. You can change your plan later in Settings.</p>

    <div className="account-start-options">
      <button type="button" onClick={onStartFree} className="account-start-option account-start-option--primary">
        <span className="account-start-option-icon" aria-hidden="true">
          <Icon name="globe" size={20} />
        </span>
        <span>
          <strong>Start free on the web</strong>
          <small>Create an Explorer account and begin with the complete manual loop.</small>
        </span>
        <Icon name="arrow-right" size={18} />
      </button>

      <button type="button" onClick={onStartLifetime} className="account-start-option">
        <span className="account-start-option-icon" aria-hidden="true">
          <Icon name="laptop" size={20} />
        </span>
        <span>
          <strong>Set up Desktop Lifetime</strong>
          <small>Create your account first, then continue to Lifetime checkout and desktop setup.</small>
        </span>
        <Icon name="arrow-right" size={18} />
      </button>
    </div>

    <p className="account-start-login">
      Already have an account?{' '}
      <button type="button" onClick={onLogin}>Log in</button>
    </p>
  </div>
);

const AuthAccessExperience = ({
  children,
  darkMode,
  mode = 'login',
  onBack,
  onOpenHelp,
  onThemeToggle
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [expandedTopic, setExpandedTopic] = useState('remember-key');
  const closeButtonRef = useRef(null);
  const launcherRef = useRef(null);

  useEffect(() => {
    if (!helpOpen) return undefined;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setHelpOpen(false);
        launcherRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [helpOpen]);

  const closeHelp = () => {
    setHelpOpen(false);
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  };

  const openFullHelp = () => {
    closeHelp();
    onOpenHelp?.();
  };

  return (
    <main className={`auth-access-page auth-access-page--${mode}`}>
      <section className="auth-access-panel" aria-label="ThryveLoop account access">
        <header className="auth-access-header">
          <button type="button" className="auth-access-brand" onClick={onBack} aria-label="Back to ThryveLoop home">
            <Logo size={34} />
            <span>ThryveLoop</span>
          </button>
          <div className="auth-access-header-actions">
            <button
              type="button"
              onClick={onThemeToggle}
              className="auth-access-icon-button"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Icon name={darkMode ? 'sun' : 'moon'} size={18} />
            </button>
            <button
              type="button"
              className="auth-access-guide-button"
              aria-label="Open setup guide"
              onClick={() => setHelpOpen(true)}
            >
              <Icon name="message-circle" size={17} />
              <span>Guide</span>
            </button>
          </div>
        </header>

        <div className="auth-access-panel-body">
          {children}
        </div>
      </section>

      <aside className="auth-access-story" aria-label="How ThryveLoop works">
        <img src="/landing/editorial-hero.jpg" alt="" aria-hidden="true" />
        <div className="auth-access-story-scrim" />
        <div className="auth-access-story-content">
          <div className="auth-access-story-brand">
            <Logo size={40} />
            <span>
              <strong>ThryveLoop</strong>
              <small>Private personal experimentation</small>
            </span>
          </div>

          <div className="auth-access-story-copy">
            <h2>Make a change. Learn what works.</h2>
            <p>Notice patterns, test one action, and decide what to keep.</p>
          </div>

          <ol className="auth-access-story-steps">
            {STORY_STEPS.map((step) => (
              <li key={step.label}>
                <span aria-hidden="true"><Icon name={step.icon} size={18} /></span>
                <div>
                  <strong>{step.label}</strong>
                  <small>{step.copy}</small>
                </div>
              </li>
            ))}
          </ol>

          <p className="auth-access-story-note">
            Your records stay under your control. AI Coach remains optional and uses context under your controls.
          </p>
        </div>
      </aside>

      <button
        ref={launcherRef}
        type="button"
        className="auth-guide-launcher"
        aria-label="Open ThryveLoop Guide"
        aria-expanded={helpOpen}
        aria-controls="thryveloop-auth-guide"
        onClick={() => setHelpOpen(true)}
      >
        <Icon name="message-circle" size={20} />
        <span>ThryveLoop Guide</span>
      </button>

      {helpOpen ? (
        <section id="thryveloop-auth-guide" className="auth-guide-panel" role="dialog" aria-modal="false" aria-labelledby="auth-guide-title">
          <header>
            <span className="auth-guide-mark" aria-hidden="true"><Logo size={32} /></span>
            <span>
              <strong id="auth-guide-title">ThryveLoop Guide</strong>
              <small>Start, sync, AI Coach, and account help</small>
            </span>
            <button ref={closeButtonRef} type="button" onClick={closeHelp} aria-label="Close ThryveLoop Guide">
              <Icon name="x" size={18} />
            </button>
          </header>

          <div className="auth-guide-topics">
            {HELP_TOPICS.map((topic) => {
              const isExpanded = expandedTopic === topic.id;
              return (
                <button
                  type="button"
                  key={topic.id}
                  aria-expanded={isExpanded}
                  onClick={() => setExpandedTopic(isExpanded ? '' : topic.id)}
                >
                  <span className="auth-guide-topic-icon" aria-hidden="true"><Icon name={topic.icon} size={17} /></span>
                  <span>
                    <strong>{topic.title}</strong>
                    <small>{isExpanded ? topic.detail : topic.summary}</small>
                  </span>
                  <Icon name="chevron-right" size={16} />
                </button>
              );
            })}
          </div>

          <footer>
            <button type="button" className="auth-guide-primary" onClick={openFullHelp}>
              <Icon name="book-open" size={17} />
              Help &amp; Guide
            </button>
            <a href="mailto:hello@traduotech.com">
              <Icon name="mail" size={17} />
              Send a message
            </a>
          </footer>
        </section>
      ) : null}
    </main>
  );
};

export default React.memo(AuthAccessExperience);
